import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leading?: ReactNode;
  trailing?: ReactNode;
  fullWidth?: boolean;
}

/**
 * Three variants. Not six. And none of them is a box.
 *
 * `.app` shipped 3 and `.men` shipped 6 (default/outline/secondary/destructive/
 * ghost/link), which is how two sister products end up with visibly unrelated
 * buttons. Everything collapses to this ladder:
 *
 *   primary   — a white label over a 2px accent rule. ONE per view.
 *   secondary — a white label. Supporting actions.
 *   tertiary  — a white label at 55%. Everything else.
 *
 * ── Controls are typographic, not boxed ────────────────────────────────────
 *
 * Screen System §5: "Primary: white label + 2px pink underline. Secondary:
 * white label. Tertiary: 55% white. Icon: no chrome; active goes pink. One
 * underlined primary per view."
 *
 * This is what the brand site has always drawn (its `.hottr-text-cta` rules)
 * and what this kit drew only as a magenta fill, because nine apps had imported
 * the fill before v3.0 and canon declined to change them underneath. It is
 * changed now, deliberately and in the open, which is why kit/README.md's
 * paragraph warning against it has been rewritten in the same commit.
 *
 * The reasoning, so it is not undone by someone who likes the old button: a
 * filled control announces itself as a widget and pulls the eye away from the
 * page. A rule under a label says "this is the action" without shouting, and
 * it keeps a page of HOTTR looking like a page of HOTTR rather than a form.
 *
 * ── Two details that are load-bearing ──────────────────────────────────────
 *
 * `border-hottr-pink`, never the hex. The accent is `--brand-accent`, and
 * HOTTR.care is a white-label surface that repaints it — a tenant on a gold
 * skin would otherwise get a magenta rule under every primary.
 *
 * `pb-px`, not `pb-0.5` or a padding utility with a size in px: it absorbs the
 * rule into the label's own line box so the text baseline does not move when a
 * control changes variant.
 *
 * Pink discipline, unchanged and now enforceable by assertion: only `primary`
 * may carry the accent. A view with two underlined primaries has none.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'text-white',
    'border-b-2 border-hottr-pink pb-px',
    'hover:border-hottr-pink/70',
  ),
  // The transparent rule is not decoration. It reserves the box the underline
  // would occupy, so swapping a secondary for a primary does not shift the row.
  secondary: cn(
    'text-white border-b-2 border-transparent pb-px',
    'hover:text-white/72',
  ),
  tertiary: cn(
    'text-white/55 border-b-2 border-transparent pb-px',
    'hover:text-white',
  ),
};

/**
 * Icon controls carry no chrome at all — the glyph IS the control, and a rule
 * under a single glyph reads as a mistake.
 *
 * Gated on SIZE rather than variant, because the estate asks for
 * `variant="primary" size="icon"` in 82 places in `.music` alone. Those are
 * icon buttons, not primaries; keying this off the variant would leave 82
 * stray underlines behind.
 *
 * `active` goes accent, which is the one place an icon is allowed to.
 */
const VARIANT_ICON = cn('text-white/72', 'hover:text-white', 'active:text-hottr-pink');

const SIZE: Record<ButtonSize, string> = {
  // `min-h`, never `h`. A fixed height CROPS the 2px rule — the box is the
  // height it is given and the rule sits on the last pixel of it.
  //
  // `px-0`: the rule is the affordance, so it runs flush to the label. Side
  // padding around an underline reads as a hyphen, not a button.
  //
  // The 44px floor is a HEIGHT floor here. A short label is narrower than 44px,
  // which is the trade canon makes for a typographic control; anything that
  // needs a full 44 wide is `size="icon"`.
  sm: 'min-h-11 px-0 text-[10px]',
  md: 'min-h-12 px-0 text-[11px]',
  lg: 'min-h-14 px-0 text-[12px]',
  // The one size that keeps a box, because 44x44 cannot be reached otherwise.
  icon: 'h-11 w-11 p-0',
};

/**
 * The button's class string, without the button.
 *
 * Exported because a HOTTR button is not always a `<button>`. Two patterns need
 * the classes on some other element, and both were silently broken across the
 * estate before this existed:
 *
 *   1. A link that looks like a button —
 *      `<Link className={buttonClasses({ variant: 'secondary' })}>`.
 *      shadcn ships a `buttonVariants()` helper for exactly this, and when the
 *      consumer apps were shimmed onto the kit their local helper was left
 *      returning an empty string. Every such call site rendered as bare text,
 *      and no build, typecheck or lint could see it — an empty string is a
 *      perfectly valid className.
 *
 *   2. `asChild` — rendering through a Slot so the caller supplies the element.
 *      Same failure: if the Slot does not receive these classes, the button
 *      disappears. `.music` alone has 34 such call sites.
 *
 * Keep this and `Button` in step. `Button` calls it, so they cannot drift.
 */
export const buttonClasses = ({
  // Secondary, not primary. See the note on Button's own default below.
  variant = 'secondary',
  size = 'md',
  fullWidth,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string =>
  cn(
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap',
    'rounded-sm hottr-cta',
    'transition-[background-color,color,border-color,box-shadow,transform] duration-base ease-mechanical',
    'active:scale-[0.97]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
    'disabled:pointer-events-none disabled:opacity-40',
    // An icon button is an icon button whatever variant it was asked for.
    size === 'icon' ? VARIANT_ICON : VARIANT[variant],
    SIZE[size],
    fullWidth && 'w-full',
    className,
  );

/**
 * ── The default variant is `secondary`, and that is the point ──────────────
 *
 * It used to be `primary`, which was survivable only while primary was a fill:
 * a button that made no claim still looked like a button. It is not survivable
 * now, because an underlined primary is a CLAIM — "this is the action on this
 * screen" — and the estate has far more unlabelled buttons than it has screens
 * with one action on them. Left alone, every view would carry three or four
 * underlined primaries and the underline would stop meaning anything.
 *
 * So emphasis is now something a call site asks for by name. A `<Button>` that
 * says nothing renders as a quiet label, which is the honest default for a
 * control whose job has not been stated, and `variant="primary"` is the one
 * thing you want tapped.
 *
 * This is the single largest behavioural change in the kit and it is why the
 * migration is being done one app at a time rather than synced everywhere at
 * once. A call site that loses its emphasis needs `variant="primary"` added,
 * and that is a decision someone should make by looking at the screen.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'secondary', size = 'md', leading, trailing, fullWidth, className, children, ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      className={buttonClasses({ variant, size, fullWidth, className })}
      {...rest}
    >
      {leading}
      {children}
      {trailing}
    </button>
  ),
);
Button.displayName = 'Button';
